import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ErrorMessage, Field, Form, Formik } from "formik";
import DatePicker from "react-date-picker";
import moment from "moment";
import { setMainLoading, setPageTitle } from "../../Store/reducers/Common";
import { PostAxios, responseCatchFunc } from "../../Services/RequestService";
import { ConvertErrorArrayToString } from "../../Services/Common";
import { setToastData } from "../../Store/reducers/Toast";
import Pagination from "../../Services/Pagination";
import { numberFormatCustom } from "../../Services/DateFunc";

const MEMBERS_PER_PAGE = 25;

const ViewCollection = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { accessToken, userData } = useSelector((state) => state.user);
    const collectionId = location.state?.collection_id;

    const [collectionData, setCollectionData] = useState(null);
    const [summary, setSummary] = useState({});
    const [membersList, setMembersList] = useState([]);
    const [membersTotal, setMembersTotal] = useState(0);
    const [membersPage, setMembersPage] = useState(1);
    const [ledgerEntries, setLedgerEntries] = useState([]);
    const [divList, setDivList] = useState([]);
    const [filterDivId, setFilterDivId] = useState(0);
    const [search, setSearch] = useState("");
    const [selectedMember, setSelectedMember] = useState(null);
    const [canSubmitPayment, setCanSubmitPayment] = useState(false);
    const [canEditCollection, setCanEditCollection] = useState(false);

    const loadCollection = ({
        divId = filterDivId,
        page = membersPage,
        memberSearch = search,
        memberUserId = selectedMember?.user_id || 0,
    } = {}) => {
        if (!collectionId) return;
        dispatch(setMainLoading(true));
        PostAxios(`collection/view-collection`, {
            collection_id: collectionId,
            div_id: divId,
            search: memberSearch,
            members_page: page,
            members_per_page: MEMBERS_PER_PAGE,
            member_user_id: memberUserId,
        }, accessToken)
            .then(({ data }) => {
                if (data.status === "1") {
                    setCollectionData(data.data);
                    setSummary(data.summary || {});
                    setMembersList(data.membersList || []);
                    setMembersTotal(data.membersTotal || 0);
                    setMembersPage(data.membersPage || page);
                    setLedgerEntries(data.ledgerEntries || []);
                    setCanSubmitPayment(!!data.canSubmitPayment);
                    setCanEditCollection(!!data.canEditCollection);
                } else {
                    dispatch(setToastData({
                        message: data.errors ? ConvertErrorArrayToString(data.errors) : data.message,
                        type: "error",
                        showCloseBtn: true,
                    }));
                    navigate("/collections");
                }
            })
            .catch((err) => responseCatchFunc(dispatch, err))
            .finally(() => dispatch(setMainLoading(false)));
    };

    useEffect(() => {
        if (!collectionId) {
            navigate("/collections");
            return;
        }
        dispatch(setPageTitle("View Collection"));
        if (userData.role_slug === "super_admin" || userData.role_slug === "org_admin") {
            PostAxios(`masters/division-list`, {
                org_id: userData.role_slug === "super_admin" ? 0 : userData.org_id,
                page: 1,
                perPage: 1000,
            }, accessToken).then(({ data }) => {
                if (data.status === "1") setDivList(data.data);
            });
        }
        loadCollection();
    }, [collectionId]);

    const collectionDivisions = useMemo(() => {
        if (!collectionData?.user_div) return [];
        const ids = String(collectionData.user_div).split(",").map((id) => parseInt(id, 10)).filter(Boolean);
        return divList.filter((d) => ids.includes(parseInt(d.ID, 10)));
    }, [collectionData, divList]);

    const scrollToMemberSection = () => {
        setTimeout(() => {
            document.getElementById("collection-member-ledger")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 400);
    };

    const openMemberLedger = (member) => {
        setSelectedMember(member);
        loadCollection({ memberUserId: member.user_id });
    };

    const openMemberEntry = (member) => {
        setSelectedMember(member);
        loadCollection({ memberUserId: member.user_id });
        scrollToMemberSection();
    };

    const closeMemberSection = () => {
        setSelectedMember(null);
        loadCollection({ memberUserId: 0 });
    };

    const submitPayment = (values, { resetForm, setSubmitting }) => {
        if (!selectedMember) return;
        dispatch(setMainLoading(true));
        setSubmitting(true);
        PostAxios(`collection/submit-payment`, {
            collection_id: collectionId,
            mem_id: selectedMember.user_id,
            div_id: selectedMember.user_div,
            amount: values.amount,
            payment_date: moment(values.payment_date).format("YYYY-MM-DD"),
            remarks: values.remarks,
        }, accessToken)
            .then(({ data }) => {
                if (data.status === "1") {
                    dispatch(setToastData({ message: data.message, type: "success", showCloseBtn: true }));
                    resetForm();
                    loadCollection({ memberUserId: selectedMember.user_id });
                } else {
                    dispatch(setToastData({
                        message: data.errors ? ConvertErrorArrayToString(data.errors) : data.message,
                        type: "error",
                        showCloseBtn: true,
                    }));
                }
            })
            .catch((err) => responseCatchFunc(dispatch, err))
            .finally(() => {
                setSubmitting(false);
                dispatch(setMainLoading(false));
            });
    };

    if (!collectionData) return null;

    const targetAmount = parseFloat(collectionData.target_amount || 0);
    const totalCollected = parseFloat(summary.total_collected || 0);
    const progressPercent = targetAmount > 0 ? Math.min(100, (totalCollected / targetAmount) * 100) : 0;

    return (
        <>
            <div className="main-form-sec">
                <div className="top-right-btn p-0">
                    {canEditCollection && (
                        <button
                            type="button"
                            className="btn btn-blue br-0 me-2"
                            onClick={() => navigate("/collections/edit", { state: { collection_id: collectionId } })}
                        >
                            Edit Collection
                        </button>
                    )}
                    <button type="button" className="btn btn-blue br-0" onClick={() => navigate("/collections")}>
                        Back to List
                    </button>
                </div>

                    <h4 className="mb-3">{collectionData.collection_name}</h4>
                <div className="form-sec">
                    <div className="w-50 p-3">Description : <b>{collectionData.collection_description || "-"}</b></div>
                    <div className="w-50 p-3">Divisions : <b>{collectionData.division_names || "-"}</b></div>
                    <div className="w-50 p-3">Target Amount : <b>{numberFormatCustom(targetAmount)}</b></div>
                    <div className="w-50 p-3">Collected : <b>{numberFormatCustom(totalCollected)}</b> ({progressPercent.toFixed(1)}%)</div>
                    <div className="w-50 p-3">End Date : <b>{moment(collectionData.end_date).format("DD MMM YYYY")}</b></div>
                    <div className="w-50 p-3">Type / Frequency : <b>{collectionData.collection_type} / {collectionData.collection_frequency}</b></div>
                    <div className="w-50 p-3">Status : <b>{parseInt(collectionData.status, 10) === 1 ? "Active" : "Inactive"}</b></div>
                    <div className="w-50 p-3">Created By : <b>{collectionData.created_by_name || "-"}</b> on <b>{moment(collectionData.created_at).format("DD MMM YYYY hh:mm A")}</b></div>
                    {collectionData.updated_at && (
                        <div className="w-50 p-3">Updated By : <b>{collectionData.updated_by_name || "-"}</b> on <b>{moment(collectionData.updated_at).format("DD MMM YYYY hh:mm A")}</b></div>
                    )}
                </div>

            </div>
            <div className="main-form-sec">
                <div className="form-sec d-flex flex-wrap gap-3 align-items-end mb-3">
                    {(userData.role_slug === "super_admin" || userData.role_slug === "org_admin") && (
                        <div className="form-field-group mb-0" style={{ minWidth: 220 }}>
                            <label>Filter Division</label>
                            <select
                                className="select-input"
                                value={filterDivId}
                                onChange={(e) => {
                                    const divId = parseInt(e.target.value, 10);
                                    setFilterDivId(divId);
                                    setMembersPage(1);
                                    loadCollection({ divId, page: 1 });
                                }}
                            >
                                <option value={0}>All Divisions</option>
                                {collectionDivisions.map((d) => (
                                    <option key={`div-${d.ID}`} value={d.ID}>{d.division_name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="form-field-group mb-0" style={{ minWidth: 260 }}>
                        <label>Search Member</label>
                        <input
                            type="text"
                            className="form-input"
                            placeholder="Name or ITS..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    setMembersPage(1);
                                    loadCollection({ page: 1, memberSearch: search });
                                }
                            }}
                        />
                    </div>
                    <button
                        type="button"
                        className="btn btn-blue"
                        onClick={() => {
                            setMembersPage(1);
                            loadCollection({ page: 1, memberSearch: search });
                        }}
                    >
                        Search
                    </button>
                </div>

                <div className="table-main">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>S.No.</th>
                                <th>Member</th>
                                <th>ITS</th>
                                <th>Division</th>
                                <th>Total Paid</th>
                                <th>Last Payment</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {membersList.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="text-center text-muted py-4">No members found.</td>
                                </tr>
                            )}
                            {membersList.map((item, index) => (
                                <tr key={`member-${item.user_id}`} className={selectedMember?.user_id === item.user_id ? "table-active" : ""}>
                                    <td>{(membersPage - 1) * MEMBERS_PER_PAGE + index + 1}</td>
                                    <td>{`${item.user_title_prefix} ${item.first_name} ${item.user_last_name}`.trim()}</td>
                                    <td>{item.user_its}</td>
                                    <td>{item.division_name}</td>
                                    <td>{numberFormatCustom(parseFloat(item.total_paid || 0))}</td>
                                    <td>{item.last_payment_date ? moment(item.last_payment_date).format("DD MMM YYYY") : "-"}</td>
                                    <td>
                                        <button type="button" className="btn btn-sm btn-blue me-1" onClick={() => openMemberLedger(item)}>
                                            Ledger
                                        </button>
                                        {canSubmitPayment && parseInt(collectionData.status, 10) === 1 && (
                                            <button type="button" className="btn btn-sm status-btn approved" style={{ float: "none" }} onClick={() => openMemberEntry(item)}>
                                                Enter Amount
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <Pagination
                    currentPage={membersPage}
                    totalCount={membersTotal}
                    pageSize={MEMBERS_PER_PAGE}
                    onPageChange={(page) => {
                        setMembersPage(page);
                        loadCollection({ page });
                    }}
                />
            </div>

            {selectedMember && (
                <div id="collection-member-ledger" className="main-form-sec">
                    <div className="d-flex justify-content-between align-items-center mb-3 w-100">
                        <h4 className="mb-0">
                            {`${selectedMember.user_title_prefix} ${selectedMember.first_name} ${selectedMember.user_last_name}`.trim()} — Ledger
                        </h4>
                        <button type="button" className="btn btn-sm btn-blue" onClick={closeMemberSection}>
                            <i className="fas fa-times" /> Close
                        </button>
                    </div>
                    {canSubmitPayment && parseInt(collectionData.status, 10) === 1 && (
                        <Formik
                            initialValues={{
                                amount: "",
                                payment_date: new Date(),
                                remarks: "",
                            }}
                            validate={(values) => {
                                const errors = {};
                                if (!values.amount || parseFloat(values.amount) <= 0) errors.amount = "Amount is required";
                                if (!values.payment_date) errors.payment_date = "Payment date is required";
                                return errors;
                            }}
                            onSubmit={submitPayment}
                        >
                            {({ errors, touched, isSubmitting, setFieldValue, values }) => (
                                <Form className="form-sec mb-4">
                                    <div className={`form-field-group w-50 ${errors.amount && touched.amount ? "error-input" : ""}`}>
                                        <div className="w-50"><label>Amount*</label></div>
                                        <div className="w-50 input-area">
                                            <Field name="amount" type="number" min="0.01" step="0.01" className="form-input" />
                                            <ErrorMessage component="span" name="amount" className="error-label" />
                                        </div>
                                    </div>
                                    <div className={`form-field-group w-50 ${errors.payment_date && touched.payment_date ? "error-input" : ""}`}>
                                        <div className="w-50"><label>Payment Date*</label></div>
                                        <div className="w-50">
                                            <div className="date-picker-sec">
                                                <DatePicker
                                                    value={values.payment_date}
                                                    onChange={(date) => setFieldValue("payment_date", date)}
                                                    format="dd/MM/yyyy"
                                                    maxDate={new Date()}
                                                    className="form-input"
                                                    dayPlaceholder="DD"
                                                    monthPlaceholder="MM"
                                                    yearPlaceholder="YYYY"
                                                />
                                            </div>
                                            <ErrorMessage component="span" name="payment_date" className="error-label" />
                                        </div>
                                    </div>
                                    <div className="form-field-group w-50">
                                        <div className="w-50"><label>Remarks</label></div>
                                        <div className="w-50 input-area">
                                            <Field name="remarks" className="form-input" />
                                        </div>
                                    </div>
                                    <div className="w-100 px-3">
                                        <button type="submit" className="btn btn-blue" disabled={isSubmitting}>Submit Payment</button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    )}
                    <div className="table-main">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>S.No.</th>
                                    <th>Date</th>
                                    <th>Amount</th>
                                    <th>Remarks</th>
                                    <th>Submitted By</th>
                                    <th>Submitted At</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ledgerEntries.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="text-center text-muted py-4">No ledger entries yet.</td>
                                    </tr>
                                )}
                                {ledgerEntries.map((entry, index) => (
                                    <tr key={`ledger-${entry.id}`}>
                                        <td>{index + 1}</td>
                                        <td>{moment(entry.payment_date).format("DD MMM YYYY")}</td>
                                        <td>{numberFormatCustom(parseFloat(entry.amount || 0))}</td>
                                        <td>{entry.remarks || "-"}</td>
                                        <td>{entry.created_by_name || "-"}</td>
                                        <td>{moment(entry.created_at).format("DD MMM YYYY hh:mm A")}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </>
    );
};

export default ViewCollection;
